import { getDateFnsLocale } from '@eventbrite/datetime-fns';
import { gettext } from '@eventbrite/i18n';
import { format as formatDateFns, isSameDay, type Locale } from 'date-fns';
import { format, formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { LocalizedDateTime } from '../../../types';

const DATE_FORMATS = {
    DATE: 'MMM d',
    TIME: 'h:mmaaa',
} as const;

/**
 * Helper function to process gettext results with type checking and fallback
 * @param result - The result from gettext call
 * @param fallback - Fallback string if result is falsy
 * @returns Processed string result
 */
const processGettextResult = (
    result: string | undefined,
    fallback: string,
): string => {
    if (typeof result === 'string' && result.length > 0) {
        return result;
    }
    return fallback;
};

/**
 * Formats timezone abbreviation using the same approach as conversion bar
 * @param utcDate - The UTC date string
 * @param timezone - The timezone string
 * @param locale - The locale for formatting
 * @returns Timezone abbreviation (e.g., "GMT-5", "GMT+1", "GMT")
 */
const formatTimezoneAbbreviation = (
    utcDate: string,
    timezone: string,
    locale: string,
): string => {
    try {
        const dateFnsLocale = getDateFnsLocale(locale);
        return formatInTimeZone(utcDate, timezone, 'zzz', {
            locale: dateFnsLocale,
        });
    } catch {
        return '';
    }
};

/**
 * Formats time string with consistent formatting (removes :00, lowercase)
 * @param date - The date to format
 * @param locale - The date-fns locale object for formatting
 * @returns Formatted time string (e.g., "7pm", "10am")
 */
const formatTimeString = (date: Date, locale: Locale): string => {
    return formatDateFns(date, DATE_FORMATS.TIME, { locale })
        .replace(':00', '')
        .toLowerCase();
};

// Configuration types for better function signatures
type DateTimeFormatConfig = {
    utcDate: string;
    timezone: string;
    locale: string;
    showTimezone?: boolean;
};

type DateTimeRangeFormatConfig = {
    startUtc: string;
    endUtc: string;
    timezone: string;
    locale: string;
    showTimezone?: boolean;
};
type GetFormattedStartDateParams = {
    eventStartDateTime: LocalizedDateTime;
    eventEndDateTime?: LocalizedDateTime | null;
    isOnlineEvent: boolean;
    locale: string;
    hideEndDate?: boolean;
};

type GetFormattedDateRangeParams = {
    eventStartDateTime: LocalizedDateTime;
    eventEndDateTime: LocalizedDateTime;
    isOnlineEvent: boolean;
    locale: string;
};

const getAttendeeTimezone = (): string | null => {
    try {
        return new Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return null;
    }
};

const getTargetTimezone = (
    isOnlineEvent: boolean,
    creatorTimezone: string,
): string => {
    if (isOnlineEvent) {
        return getAttendeeTimezone() || creatorTimezone;
    }
    return creatorTimezone;
};

/**
 * Format brief date with time (similar to event-datetime's getFormattedBriefDateTime)
 * Returns formats like "Nov 18 at 7pm" or "Nov 18 at 7pm EST"
 */
export const formatBriefDateTime = (config: DateTimeFormatConfig): string => {
    try {
        const { utcDate, timezone, locale, showTimezone = false } = config;
        const zonedDate = utcToZonedTime(utcDate, timezone);
        const dateFnsLocale = getDateFnsLocale(locale);

        const formattedDate = formatDateFns(zonedDate, DATE_FORMATS.DATE, {
            locale: dateFnsLocale,
        });
        const formattedTime = formatTimeString(zonedDate, dateFnsLocale);

        if (showTimezone) {
            const timezoneAbbr = formatTimezoneAbbreviation(
                utcDate,
                timezone,
                locale,
            );

            const result = gettext('%(date)s at %(time)s %(timezone)s', {
                date: formattedDate,
                time: formattedTime,
                timezone: timezoneAbbr,
            });
            return processGettextResult(
                result.toString(),
                `${formattedDate} at ${formattedTime} ${timezoneAbbr}`,
            );
        }

        const result = gettext('%(date)s at %(time)s', {
            date: formattedDate,
            time: formattedTime,
        });
        return processGettextResult(
            result.toString(),
            `${formattedDate} at ${formattedTime}`,
        );
    } catch {
        return '';
    }
};

/**
 * Format brief date range (similar to event-datetime's getFormattedBriefDateRange)
 * Returns formats like "Nov 18 to Nov 20"
 */
export const formatBriefDateRange = (
    config: Omit<DateTimeRangeFormatConfig, 'showTimezone'>,
): string => {
    try {
        const { startUtc, endUtc, timezone, locale } = config;
        const startDate = utcToZonedTime(startUtc, timezone);
        const endDate = utcToZonedTime(endUtc, timezone);
        const dateFnsLocale = getDateFnsLocale(locale);

        const formattedStart = formatDateFns(startDate, DATE_FORMATS.DATE, {
            locale: dateFnsLocale,
        });
        const formattedEnd = formatDateFns(endDate, DATE_FORMATS.DATE, {
            locale: dateFnsLocale,
        });

        const result = gettext('%(start)s to %(end)s', {
            start: formattedStart,
            end: formattedEnd,
        });
        return processGettextResult(
            result.toString(),
            `${formattedStart} to ${formattedEnd}`,
        );
    } catch {
        return '';
    }
};

/**
 * Format same-day event with time range
 * Returns formats like "Dec 25 from 10am to 12pm" or "Dec 25 from 10am to 12pm EST"
 */
export const formatSameDayTimeRange = (
    config: DateTimeRangeFormatConfig,
): string => {
    try {
        const {
            startUtc,
            endUtc,
            timezone,
            locale,
            showTimezone = false,
        } = config;
        const startDate = utcToZonedTime(startUtc, timezone);
        const endDate = utcToZonedTime(endUtc, timezone);
        const dateFnsLocale = getDateFnsLocale(locale);

        const formattedDate = formatDateFns(startDate, DATE_FORMATS.DATE, {
            locale: dateFnsLocale,
        });
        const formattedStartTime = formatTimeString(startDate, dateFnsLocale);
        const formattedEndTime = formatTimeString(endDate, dateFnsLocale);

        if (showTimezone) {
            const timezoneAbbr = formatTimezoneAbbreviation(
                startUtc,
                timezone,
                locale,
            );

            const result = gettext(
                '%(date)s from %(startTime)s to %(endTime)s %(timezone)s',
                {
                    date: formattedDate,
                    startTime: formattedStartTime,
                    endTime: formattedEndTime,
                    timezone: timezoneAbbr,
                },
            );

            return processGettextResult(
                result.toString(),
                `${formattedDate} from ${formattedStartTime} to ${formattedEndTime} ${timezoneAbbr}`,
            );
        }

        const result = gettext('%(date)s from %(startTime)s to %(endTime)s', {
            date: formattedDate,
            startTime: formattedStartTime,
            endTime: formattedEndTime,
        });
        return processGettextResult(
            result.toString(),
            `${formattedDate} from ${formattedStartTime} to ${formattedEndTime}`,
        );
    } catch {
        return '';
    }
};

/**
 * Format brief date range with time (similar to formatBriefDateTime but for ranges)
 * Returns formats like "Nov 18 at 7pm to Nov 19 at 8pm EST" (timezone always shown in main app)
 */
export const formatBriefDateTimeRange = (
    config: DateTimeRangeFormatConfig,
): string => {
    try {
        const {
            startUtc,
            endUtc,
            timezone,
            locale,
            showTimezone = false,
        } = config;
        const startDate = utcToZonedTime(startUtc, timezone);
        const endDate = utcToZonedTime(endUtc, timezone);
        const dateFnsLocale = getDateFnsLocale(locale);

        const formattedStartDate = formatDateFns(startDate, DATE_FORMATS.DATE, {
            locale: dateFnsLocale,
        });
        const formattedStartTime = formatTimeString(startDate, dateFnsLocale);

        const formattedEndDate = formatDateFns(endDate, DATE_FORMATS.DATE, {
            locale: dateFnsLocale,
        });
        const formattedEndTime = formatTimeString(endDate, dateFnsLocale);

        if (showTimezone) {
            const timezoneAbbr = formatTimezoneAbbreviation(
                startUtc,
                timezone,
                locale,
            );

            const result = gettext(
                '%(startDate)s at %(startTime)s to %(endDate)s at %(endTime)s %(timezone)s',
                {
                    startDate: formattedStartDate,
                    startTime: formattedStartTime,
                    endDate: formattedEndDate,
                    endTime: formattedEndTime,
                    timezone: timezoneAbbr,
                },
            );
            return processGettextResult(
                result.toString(),
                `${formattedStartDate} at ${formattedStartTime} to ${formattedEndDate} at ${formattedEndTime} ${timezoneAbbr}`,
            );
        }

        const result = gettext(
            '%(startDate)s at %(startTime)s to %(endDate)s at %(endTime)s',
            {
                startDate: formattedStartDate,
                startTime: formattedStartTime,
                endDate: formattedEndDate,
                endTime: formattedEndTime,
            },
        );
        return processGettextResult(
            result.toString(),
            `${formattedStartDate} at ${formattedStartTime} to ${formattedEndDate} at ${formattedEndTime}`,
        );
    } catch {
        return '';
    }
};

/**
 * Checks if two dates are on the same day in the appropriate timezone
 * For online events, uses attendee's local timezone
 * For in-person events, uses creator's timezone
 */
export const isSameDayInTimezone = (
    start: LocalizedDateTime,
    end: LocalizedDateTime,
    isOnlineEvent: boolean,
): boolean => {
    // For online events, compare in attendee's timezone (UTC, will be converted to local)
    // For in-person events, compare in creator's timezone
    if (isOnlineEvent) {
        // Use UTC times directly to compare in attendee's local timezone
        const startDate = new Date(start.utc);
        const endDate = new Date(end.utc);
        return isSameDay(startDate, endDate);
    } else {
        // Use creator's timezone
        const timezone = start.timezone;
        const startDate = utcToZonedTime(start.utc, timezone);
        const endDate = utcToZonedTime(end.utc, timezone);
        return isSameDay(startDate, endDate);
    }
};

export const getFormattedStartDate = ({
    eventStartDateTime,
    eventEndDateTime,
    isOnlineEvent,
    locale,
    hideEndDate = false,
}: GetFormattedStartDateParams) => {
    const targetTimezone = getTargetTimezone(
        isOnlineEvent,
        eventStartDateTime.timezone,
    );

    // Check if it's a same-day event with end time and end date is not hidden
    const isSameDayEvent =
        eventEndDateTime &&
        !hideEndDate &&
        isSameDayInTimezone(
            eventStartDateTime,
            eventEndDateTime,
            isOnlineEvent,
        );

    let formattedDateTime: string;

    if (isSameDayEvent) {
        // Use time range format for same-day events: "Dec 25 from 10am to 12pm EST"
        formattedDateTime = formatSameDayTimeRange({
            startUtc: eventStartDateTime.utc,
            endUtc: eventEndDateTime.utc,
            timezone: targetTimezone,
            locale,
            showTimezone: true, // Always show timezone
        });
    } else {
        // Use single time format: "Dec 25 at 10am EST"
        formattedDateTime = formatBriefDateTime({
            utcDate: eventStartDateTime.utc,
            timezone: targetTimezone,
            locale,
            showTimezone: true, // Always show timezone
        });
    }

    // Create dateTime attribute for the <time> tag
    // For in-person events, use local date directly
    // For online events, convert from UTC to attendee timezone
    const dateTime = isOnlineEvent
        ? format(
              utcToZonedTime(eventStartDateTime.utc, targetTimezone),
              'yyyy-MM-dd',
              { timeZone: targetTimezone },
          )
        : format(new Date(eventStartDateTime.local), 'yyyy-MM-dd');

    return { formattedDateTime, dateTime };
};

export const getFormattedDateRange = ({
    eventStartDateTime,
    eventEndDateTime,
    isOnlineEvent,
    locale,
}: GetFormattedDateRangeParams) => {
    const targetTimezone = getTargetTimezone(
        isOnlineEvent,
        eventStartDateTime.timezone,
    );

    const formattedDateTime = formatBriefDateTimeRange({
        startUtc: eventStartDateTime.utc,
        endUtc: eventEndDateTime.utc,
        timezone: targetTimezone,
        locale,
        showTimezone: true, // Always show timezone
    });

    // Create dateTime attribute for the <time> tag (use start date)
    const zonedDate = utcToZonedTime(eventStartDateTime.utc, targetTimezone);
    const dateTime = format(zonedDate, 'yyyy-MM-dd', {
        timeZone: targetTimezone,
    });

    return { formattedDateTime, dateTime };
};

export const getMultipleDatesFlag = (
    isListingTimedEntryExperimentActive: boolean,
    isSeriesParent: boolean,
    isRepeating: boolean,
): boolean => {
    return isListingTimedEntryExperimentActive || isSeriesParent || isRepeating;
};
